import { apiUrl } from "./config";

const endpoint = `${apiUrl}/pdf/personal-handwriting`;

const errorResult = (result, status, fallback) => ({
  success: false,
  status,
  code: result?.code,
  error: result?.error || result?.detail || fallback,
  missing_characters: Array.isArray(result?.missing_characters) ? result.missing_characters : [],
});

const attachmentName = (response, fallback) => {
  const disposition = response.headers?.get("Content-Disposition") || "";
  let name = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (name) {
    try { name = decodeURIComponent(name); } catch { name = ""; }
  } else name = disposition.match(/filename="([^"]+)"/i)?.[1];
  return name ? Array.from(name).map((character) =>
    character === "/" || character === "\\" || character.charCodeAt(0) < 32 ? "_" : character,
  ).join("") : fallback;
};

async function request({ path, authToken, signal, body, binary = false, filename }) {
  if (!authToken) return errorResult({}, 401, "請先登入，再使用轉手寫。");
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (signal?.aborted) controller.abort();
  else signal?.addEventListener("abort", abort, { once: true });
  let timedOut = false;
  const timeout = setTimeout(() => { timedOut = true; controller.abort(); }, 60000);
  try {
    const response = await fetch(`${endpoint}${path}`, {
      method: body ? "POST" : "GET",
      headers: {
        Authorization: `Bearer ${authToken}`,
        ...(body ? { "Content-Type": "application/json" } : {}),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
      signal: controller.signal,
    });
    if (binary && response.ok) {
      return { success: true, blob: await response.blob(), filename: attachmentName(response, filename) };
    }
    const result = await response.json().catch(() => ({}));
    if (response.ok && result.success) return result;
    return errorResult(result, response.status, "轉手寫處理失敗，請重試。");
  } catch {
    if (controller.signal.aborted) {
      return errorResult({ code: timedOut ? "timeout" : "aborted" }, 0,
        timedOut ? "等待超時，請縮短內容後重試。" : "已取消處理。");
    }
    return errorResult({}, 0, "無法連線到轉手寫服務，請重試。");
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener("abort", abort);
  }
}

export const getPersonalHandwritingProfile = ({ authToken, signal }) =>
  request({ path: "/profile/", authToken, signal });

export const previewPersonalHandwriting = ({ markdown, profileId = "wenkai", authToken, signal }) =>
  request({ path: "/preview/", body: { markdown, profile_id: profileId }, authToken, signal });

export const exportPersonalHandwriting = ({ markdown, format, profileId = "wenkai", authToken, signal }) => {
  if (!["pdf", "png"].includes(format)) return Promise.resolve(errorResult({}, 400, "不支援此匯出格式。"));
  return request({ path: "/export/", body: { markdown, format, profile_id: profileId }, authToken, signal,
    binary: true, filename: `${profileId}-handwriting.${format}` });
};

export const downloadPersonalHandwritingFont = ({ format, profileId = "wenkai", authToken, signal }) => {
  if (!["ttf", "woff2"].includes(format)) return Promise.resolve(errorResult({}, 400, "不支援此字型格式。"));
  return request({ path: `/font/?profile_id=${encodeURIComponent(profileId)}&format=${format}`, authToken, signal,
    binary: true, filename: `${profileId}.${format}` });
};
